import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { CustomToggle } from "./CustomToggle";
import { ServerCog } from "lucide-react";
import axios from "axios";
import authHeader from "../../../Auth/authHeader";
import { getAuthData } from "../../../Auth/getAuthData";
import { ApiLink } from "../../../APILink/ApiLink";
import "./SystemSettingsTab.scss";

export default function SystemSettingsTab({ initialData, onUpdate }) {
  const authData = getAuthData();
  const userCompanyUnitID = Number(authData?.companyUnitId || 0);
  const isCompanyUnitUser = userCompanyUnitID > 0;
  const [data, setData] = useState(initialData || {});
  const [savingKey, setSavingKey] = useState(null);
  const dateSaveTimerRef = useRef(null);

  useEffect(() => {
    setData(initialData || {});
  }, [initialData]);

  const saveCompanySystemSettings = async (payload) => {
    const headers = authHeader();
    await axios.put(ApiLink.API_URL + "Companies/updateSystemSettings", payload, { headers });
  };

  const saveUnitSystemSettings = async (payload) => {
    const headers = authHeader();
    await axios.put(ApiLink.API_URL + "CompanyUnits/updateSystemSettings", payload, { headers });
  };

  useEffect(() => {
    return () => {
      if (dateSaveTimerRef.current) {
        clearTimeout(dateSaveTimerRef.current);
      }
    };
  }, []);

  const handleToggle = async (key, value, title) => {
    if (savingKey) return;
    const newData = { ...data, [key]: value };
    setData(newData);
    setSavingKey(key);

    try {
      const headers = authHeader();

      if (key === "useElectronicStamp") {
        if (isCompanyUnitUser) {
          await saveUnitSystemSettings({
            UnitID: userCompanyUnitID,
            ElectronicSeal: !!value,
            HasElectronicSeal: true,
            HasNegativeStockBlock: false,
            HasPeriodClosingLockDate: false,
          });
        } else if (newData.companyId) {
          await axios.put(
            ApiLink.API_URL + "Companies/controlElectronicSeal",
            { CompanyID: newData.companyId, ElectronicSeal: !!value },
            { headers },
          );
        }
      }

      if (key === "negativeStockBlock") {
        if (isCompanyUnitUser) {
          await saveUnitSystemSettings({
            UnitID: userCompanyUnitID,
            NegativeStockBlock: !!newData.negativeStockBlock,
            HasElectronicSeal: false,
            HasNegativeStockBlock: true,
            HasPeriodClosingLockDate: false,
          });
        } else if (newData.companyId) {
          await saveCompanySystemSettings({
            CompanyID: newData.companyId,
            NegativeStockBlock: !!newData.negativeStockBlock,
            HasNegativeStockBlock: true,
            HasPeriodClosingLockDate: false,
          });
        }
      }

      const updatedRecord = { ...newData, id: newData.id || newData.companyId };
      setData(updatedRecord);
      onUpdate(updatedRecord);

      if (value) {
        toast.success(`${title} — u aktivizua me sukses!`, {
          duration: 3500,
          style: {
            background: "var(--cc-kpi-positive-bg)",
            color: "var(--cc-kpi-positive-text)",
            border: "1px solid var(--cc-kpi-positive-ring)",
            fontWeight: "600",
            fontSize: "0.875rem",
          },
          iconTheme: { primary: "var(--cc-kpi-positive-text)", secondary: "var(--cc-kpi-positive-bg)" },
        });
      } else {
        toast(`${title} — u çaktivizua.`, {
          duration: 3500,
          icon: "⛔",
          style: {
            background: "var(--cc-kpi-warning-bg)",
            color: "var(--cc-kpi-warning-text)",
            border: "1px solid var(--cc-kpi-warning-ring)",
            fontWeight: "600",
            fontSize: "0.875rem",
          },
        });
      }
    } catch (error) {
      setData({ ...data });
      toast.error(`Ndryshimi i "${title}" nuk u ruajt. Provo perseri.`, {
        duration: 4000,
        style: {
          background: "var(--cc-kpi-negative-bg)",
          color: "var(--cc-kpi-negative-text)",
          border: "1px solid var(--cc-kpi-negative-ring)",
          fontWeight: "600",
          fontSize: "0.875rem",
        },
        iconTheme: { primary: "var(--cc-kpi-negative-text)", secondary: "var(--cc-kpi-negative-bg)" },
      });
    } finally {
      setSavingKey(null);
    }
  };

  const handleDateChange = (key, value, title) => {
    const updatedRecord = {
      ...data,
      [key]: value,
      id: data.id || data.companyId,
    };

    setData(updatedRecord);
    onUpdate(updatedRecord);

    if (dateSaveTimerRef.current) {
      clearTimeout(dateSaveTimerRef.current);
    }

    dateSaveTimerRef.current = setTimeout(async () => {
      if (!updatedRecord.companyId && !isCompanyUnitUser) return;

      setSavingKey(key);
      try {
        if (isCompanyUnitUser) {
          await saveUnitSystemSettings({
            UnitID: userCompanyUnitID,
            PeriodClosingLockDate: updatedRecord.periodClosingLockDate || null,
            HasElectronicSeal: false,
            HasNegativeStockBlock: false,
            HasPeriodClosingLockDate: true,
          });
        } else {
          await saveCompanySystemSettings({
            CompanyID: updatedRecord.companyId,
            PeriodClosingLockDate: updatedRecord.periodClosingLockDate || null,
            HasNegativeStockBlock: false,
            HasPeriodClosingLockDate: true,
          });
        }

        toast.success(`${title} — u perditesua me sukses!`, {
          duration: 3500,
          style: {
            background: "var(--cc-kpi-positive-bg)",
            color: "var(--cc-kpi-positive-text)",
            border: "1px solid var(--cc-kpi-positive-ring)",
            fontWeight: "600",
            fontSize: "0.875rem",
          },
          iconTheme: { primary: "var(--cc-kpi-positive-text)", secondary: "var(--cc-kpi-positive-bg)" },
        });
      } catch (error) {
        toast.error(`Ndryshimi i "${title}" nuk u ruajt. Provo perseri.`, {
          duration: 4000,
          style: {
            background: "var(--cc-kpi-negative-bg)",
            color: "var(--cc-kpi-negative-text)",
            border: "1px solid var(--cc-kpi-negative-ring)",
            fontWeight: "600",
            fontSize: "0.875rem",
          },
          iconTheme: { primary: "var(--cc-kpi-negative-text)", secondary: "var(--cc-kpi-negative-bg)" },
        });
      } finally {
        setSavingKey(null);
      }
    }, 500);
  };

  const settingsOptions = [
    {
      id: "useElectronicStamp",
      type: "toggle",
      title: "Perdor Vule Elektronike",
      description:
        "Aktivizo perdorimin e vules elektronike ne faturat PDF te gjeneruara.",
    },
    {
      id: "negativeStockBlock",
      type: "toggle",
      title: "Aktivizo Bllokimin e shitjes per Stok Negativ",
      description:
        "Blloko veprimet e shitjes, kur artikulli i caktuar ka stok negativ.",
    },
    {
      id: "periodClosingLockDate",
      type: "date",
      title: "Mbyll Periudhën e Transaksioneve",
      description:
        "Bllokon krijimin dhe modifikimin e transaksioneve për periudhat e mbyllura (p.sh. para 31/03/2026).",
    },
    // {
    //   id: "autoInvoiceNumbering",
    //   title: "Aktivizo Numerimin Automatik te Faturave",
    //   description: "Sistemi do te caktoje automatikisht numra rendor per faturat e reja."
    // },
    // {
    //   id: "multiCurrency",
    //   title: "Aktivizo Shume-Valuten",
    //   description: "Lejo transaksione dhe raporte ne disa valuta te huaja."
    // },
    // {
    //   id: "discountApproval",
    //   title: "Aktivizo Aprovimin e Zbritjeve",
    //   description: "Kerko aprovimin e menaxherit per zbritje mbi kufirin standard."
    // }
  ];

  const formatBool = (value) => (value ? "Po" : "Jo");

  const readonlyCompanyReference = isCompanyUnitUser
    ? [
        {
          id: "useElectronicStamp",
          title: "Perdor Vule Elektronike",
          value: formatBool(!!data?.companyReference?.useElectronicStamp),
        },
        {
          id: "negativeStockBlock",
          title: "Bllokim i shitjes per Stok Negativ",
          value: formatBool(!!data?.companyReference?.negativeStockBlock),
        },
        {
          id: "periodClosingLockDate",
          title: "Mbyllja e Periudhes se Transaksioneve",
          value: data?.companyReference?.periodClosingLockDate || "Pa date te vendosur",
        },
      ]
    : [];

  return (
    <div className="system-settings-tab">
      <div className="system-settings-tab__header">
        <h2 className="system-settings-tab__title">
          <ServerCog className="system-settings-tab__title-icon" />
          Rregullimet e Perdorimit te Sistemit
        </h2>
        <p className="system-settings-tab__subtitle">
          Konfiguro sjelljen e aplikacionit dhe rregullat globale te operimit.
          Ndryshimet ruhen automatikisht.
        </p>
      </div>

      <div className="system-settings-tab__list">
        {settingsOptions.map((opt) => (
          <div key={opt.id} className="system-settings-tab__row">
            <div className="system-settings-tab__row-content">
              <label htmlFor={opt.id} className="system-settings-tab__label">
                {opt.title}
              </label>
              <p className="system-settings-tab__description">
                {opt.description}
              </p>
            </div>
            {opt.type === "date" ? (
              <div className="system-settings-tab__date-wrap">
                <input
                  id={opt.id}
                  type="date"
                  value={data[opt.id] || ""}
                  onChange={(e) => handleDateChange(opt.id, e.target.value, opt.title)}
                  className="system-settings-tab__date-input"
                  disabled={savingKey !== null}
                />
              </div>
            ) : (
              <div className="system-settings-tab__toggle-wrap">
                <CustomToggle
                  id={opt.id}
                  checked={!!data[opt.id]}
                  disabled={savingKey !== null}
                  onCheckedChange={(val) => handleToggle(opt.id, val, opt.title)}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {isCompanyUnitUser && readonlyCompanyReference.length > 0 && (
        <div className="system-settings-tab__reference">
          <div className="system-settings-tab__reference-header">
            Vlerat e Kompanise (Reference Vetem Lexim)
          </div>
          <div className="system-settings-tab__reference-list">
            {readonlyCompanyReference.map((item) => (
              <div key={item.id} className="system-settings-tab__reference-row">
                <span className="system-settings-tab__reference-label">{item.title}</span>
                <span className="system-settings-tab__reference-value">{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
